import type { ComponentProps } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/styles';

/** Props for the `Empty` root container. */
export type EmptyProps = ComponentProps<'div'>;

export function Empty({ className, ...props }: EmptyProps) {
  return (
    <div
      data-slot="empty"
      className={cn(
        'gap-4 rounded-lg border-dashed p-12 flex w-full min-w-0 flex-1 flex-col items-center justify-center text-center text-balance',
        className,
      )}
      {...props}
    />
  );
}

/** Props for the `EmptyHeader` section that groups media, title, and description. */
export type EmptyHeaderProps = ComponentProps<'div'>;

export function EmptyHeader({ className, ...props }: EmptyHeaderProps) {
  return (
    <div data-slot="empty-header" className={cn('gap-2 flex max-w-sm flex-col items-center', className)} {...props} />
  );
}

const emptyMediaVariants = cva(
  'mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /** Transparent background — use for custom illustrations or emoji. */
        default: 'bg-transparent',
        /** Muted rounded square background sized for a single icon (`size-10`). */
        icon: "bg-muted text-foreground flex size-10 shrink-0 items-center justify-center rounded-lg [&_svg:not([class*='size-'])]:size-6",
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/** Props for the `EmptyMedia` slot that holds an icon or illustration. */
export type EmptyMediaProps = ComponentProps<'div'> & VariantProps<typeof emptyMediaVariants>;

export function EmptyMedia({ className, variant = 'default', ...props }: EmptyMediaProps) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  );
}

/** Props for the `EmptyTitle` heading element. */
export type EmptyTitleProps = ComponentProps<'div'>;

export function EmptyTitle({ className, ...props }: EmptyTitleProps) {
  return <div data-slot="empty-title" className={cn('text-lg font-medium tracking-tight', className)} {...props} />;
}

/** Props for the `EmptyDescription` supporting text element. */
export type EmptyDescriptionProps = ComponentProps<'p'>;

export function EmptyDescription({ className, ...props }: EmptyDescriptionProps) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        'text-sm/relaxed text-muted-foreground [&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4',
        className,
      )}
      {...props}
    />
  );
}

/** Props for the `EmptyContent` slot that holds action buttons or supplemental content. */
export type EmptyContentProps = ComponentProps<'div'>;

export function EmptyContent({ className, ...props }: EmptyContentProps) {
  return (
    <div
      data-slot="empty-content"
      className={cn('gap-4 text-sm flex w-full max-w-sm min-w-0 flex-col items-center text-balance', className)}
      {...props}
    />
  );
}
